import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLines, FadeUp } from "../components/Reveal";
import CoverImage from "../components/CoverImage";
import { useContent } from "../context/ContentContext";
import { secStyle, pageHeroClass, overlineColorClass, focalStyle } from "../lib/layout";
import { coverSrc, heroSrcSet } from "../lib/images";

export default function Curriculums() {
  const [items, setItems] = useState([]);
  const { content } = useContent();
  const curImg = coverSrc(content.pages?.curriculums?.image, "hero");
  const curFocal = content.pages?.curriculums?.focal;
  const curHero = content.pages?.curriculums || {};
  const curStyle = focalStyle(curFocal);

  useEffect(() => {
    api.get("/curriculums").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="curriculums-page">
      {/* Hero — artwork behind the title, matching the Home hero */}
      <section className={`relative overflow-hidden flex flex-col justify-end pt-16 lg:pt-20 ${pageHeroClass(content.pages?.curriculums?.hero_height)}`} data-testid="curriculums-hero">
        <div className="absolute inset-0 z-0">
          {curImg && <img src={curImg} srcSet={heroSrcSet(content.pages?.curriculums?.image)} sizes="100vw" alt="" style={curStyle} fetchPriority="high" decoding="async" className="w-full h-full object-cover hero-img gpu-layer" />}
          <div className="absolute inset-0 hero-overlay" />
          {(curFocal?.veil ?? 0) > 0 && <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: curFocal.veil / 100 }} />}
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 lg:px-12 pt-6 pb-12 lg:pb-16 hero-text">
          <h1 className="font-serif font-light tracking-tighter leading-[0.88] text-vellum text-[clamp(3rem,11vw,9rem)]">
            <MaskedLines lines={[curHero.title_line1 || "Personal"]} />
            <MaskedLines lines={[curHero.title_line2 || "Curriculums"]} delay={0.15} lineClassName="italic text-brass" />
          </h1>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-xl text-vellum-muted text-lg leading-relaxed">
              {curHero.subtitle}
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <p className={`overline mt-8 ${overlineColorClass(curHero.overline_color)}`} data-testid="curriculums-overline">{curHero.overline || "Learning Pathways · Sign in to track progress"}</p>
          </FadeUp>
        </div>
      </section>

      <section style={secStyle(content, "curriculums_list")}>
        {items.map((c, i) => (
          <FadeUp key={c.id}>
            <Link
              to={`/curriculums/${c.id}`}
              data-testid={`curriculum-card-${c.id}`}
              className="group block border-b border-line hover:bg-ink-800 transition-colors duration-500"
            >
              <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-14 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="relative lg:col-span-3 overflow-hidden aspect-[4/3] border border-line">
                  <CoverImage src={c.image_url} alt={c.title} focal={c.image_focal} size="detail" title={c.title} />
                </div>
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="font-mono text-xs text-crimson">{String(i + 1).padStart(2, "0")}</span>
                    <span className="overline text-brass">{c.field}</span>
                    <span className="font-mono text-[10px] text-vellum-faint">{c.modules?.length || 0} modules · {(c.modules || []).reduce((a, m) => a + (m.steps?.length || 0), 0)} steps</span>
                  </div>
                  <h2 className="font-serif text-4xl lg:text-6xl font-light tracking-tight text-vellum group-hover:text-brass transition-colors">{c.title}</h2>
                  <p className="mt-4 max-w-2xl text-vellum-muted leading-relaxed line-clamp-2">{c.description}</p>
                </div>
                <div className="lg:col-span-2 flex lg:justify-end">
                  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-vellum-muted group-hover:text-crimson transition-colors">
                    Enter <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          </FadeUp>
        ))}
      </section>
    </div>
  );
}
